import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import Admin from "../layout/Admin";
import spinner from "../layout/spinner.gif";
import { getTemplate, saveTemplate, previewTemplate, getThemes } from "../../actions/template";
import {
  STATUSES,
  FIELD_TYPES,
  COLUMN_TYPES,
  ORIENTATIONS,
  MARGINS,
  CATEGORY_ICONS,
  fieldClass,
  areaClass,
  labelClass,
  primaryButton,
  secondaryButton,
  smallButton,
} from "./templateLabels";

// /template-edit?id=<id> edits a template, /template-edit makes a new one. The
// preview is the real PDF from the backend, filled with sample values.

const EMPTY_FIELD = {
  key: "",
  label: "",
  type: "text",
  required: false,
  maxLength: "",
  options: [],
  placeholder: "",
  help: "",
  // Only for rows: its columns, the most lines, and the number columns a line's
  // {{row_total}} multiplies
  columns: [],
  maxRows: "",
  total: [],
};

const EMPTY_COLUMN = {
  key: "",
  label: "",
  type: "text",
  required: false,
  maxLength: "",
  options: [],
  placeholder: "",
};

// A rows field starts with one column so the idea shows at once
const NEW_ROWS = {
  columns: [{ ...EMPTY_COLUMN, key: "name", label: "Нэр", required: true }],
  maxRows: "",
  total: [],
};

// The placeholders a field gives the body besides {{key}} (utils/templateDoc.js)
const extraKeysOf = (field) => {
  if (field.type === "number") return [field.key + "_words"];
  if (field.type !== "rows") return [];
  return [
    field.key + "_count",
    ...(field.total.length ? [field.key + "_total", field.key + "_total_words", field.key + "_vat"] : []),
  ];
};

// What {{row_total}} can multiply: each number column, and each pair of them
const totalChoicesOf = (columns) => {
  const numbers = columns.filter((column) => column.type === "number" && column.key).map((column) => column.key);
  const pairs = numbers.flatMap((a, i) => numbers.slice(i + 1).map((b) => [a, b]));
  return [...numbers.map((key) => [key]), ...pairs];
};

// A new template starts with one field so the {{key}} idea shows at once
const NEW_TEMPLATE = {
  fields: [
    { ...EMPTY_FIELD, key: "full_name", label: "Овог нэр", required: true, placeholder: "Б.Болд" },
  ],
  body: "<p>{{full_name}}</p>",
};

// Numbers are kept as text so a box can be emptied while typing
const toForm = (template) => ({
  name: template.name || "",
  slug: template.slug || "",
  category: template.category || "",
  description: template.description || "",
  order: String(template.order ?? 0),
  price: template.price === null || template.price === undefined ? "" : String(template.price),
  fields: (template.fields || []).map((field) => ({
    ...EMPTY_FIELD,
    ...field,
    maxLength: field.maxLength ? String(field.maxLength) : "",
    options: field.options || [],
    columns: (field.columns || []).map((column) => ({
      ...EMPTY_COLUMN,
      ...column,
      maxLength: column.maxLength ? String(column.maxLength) : "",
      options: column.options || [],
    })),
    maxRows: field.maxRows ? String(field.maxRows) : "",
    total: field.total || [],
  })),
  body: template.body || "",
  orientation: template.orientation || "portrait",
  paperMedia: template.paperMedia || "plain-a4",
  margins: template.margins || "official",
  // Slugs of the designs the customer picks from; the first is picked for them
  themes: template.themes || [],
});

const PARAM_LABELS = {
  name: "Нэр",
  slug: "Slug",
  category: "Ангилал",
  description: "Тайлбар",
  price: "Үнэ",
  paperMedia: "Цаас",
  order: "Дараалал",
  fields: "Талбар",
  body: "Агуулга",
  orientation: "Хуудасны чиглэл",
  margins: "Захын зай",
  themes: "Дизайн",
};

const Section = ({ title, children, right }) => (
  <div className="p-4 mb-6 bg-white rounded-lg shadow-md dark:bg-gray-800">
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">{title}</h3>
      {right}
    </div>
    {children}
  </div>
);

const TextBox = ({ label, hint, ...props }) => (
  <label className="block">
    <span className={labelClass}>{label}</span>
    <input className={fieldClass} {...props} />
    {hint && <span className="block mt-1 text-xs text-gray-500 dark:text-gray-400">{hint}</span>}
  </label>
);

// A table for the body with a heading per column and the rows section, so the admin
// starts from something that works
const tableOf = (field) => {
  const total = field.total.length > 0;
  const columns = field.columns.filter((column) => column.key);
  const widths = JSON.stringify({
    widths: [20, ...columns.map(() => "*"), ...(total ? [80] : [])],
    headerRows: 1,
  });
  const heads = ["№", ...columns.map((column) => column.label || column.key), ...(total ? ["Дүн"] : [])];
  const cells = ["{{n}}", ...columns.map((column) => "{{" + column.key + "}}"), ...(total ? ["{{row_total}}"] : [])];
  return (
    `<table data-pdfmake='${widths}'>\n<thead><tr>` +
    heads.map((head) => `<th>${head}</th>`).join("") +
    `</tr></thead>\n<tbody>{{#${field.key}}}<tr>` +
    cells.map((cell) => `<td>${cell}</td>`).join("") +
    `</tr>{{/${field.key}}}</tbody>\n</table>` +
    (total ? `\n<p>Нийт дүн: {{${field.key}_total}} ({{${field.key}_total_words}}) төгрөг</p>` : "")
  );
};

// The columns of a rows field, how many lines it may have and what a line's amount
// multiplies. changes go into the field.
const ColumnsEditor = ({ field, onChange, insert }) => {
  const setColumns = (columns) => {
    const numbers = columns.filter((column) => column.type === "number").map((column) => column.key);
    onChange({ columns, total: field.total.filter((key) => numbers.includes(key)) });
  };
  const setColumn = (index, changes) =>
    setColumns(field.columns.map((column, i) => (i === index ? { ...column, ...changes } : column)));
  const moveColumn = (index, delta) => {
    const columns = [...field.columns];
    const [column] = columns.splice(index, 1);
    columns.splice(index + delta, 0, column);
    setColumns(columns);
  };
  const choices = totalChoicesOf(field.columns);
  const key = field.key || "key";

  return (
    <div className="col-span-2 p-3 rounded-lg md:col-span-6 bg-gray-50 dark:bg-gray-900">
      <div className="flex items-center justify-between mb-2">
        <span className={labelClass}>Баганууд: мөр бүрт бөглөнө</span>
        <button
          type="button"
          className={smallButton}
          disabled={field.columns.length >= 8}
          onClick={() => setColumns([...field.columns, { ...EMPTY_COLUMN }])}
        >
          + Багана
        </button>
      </div>
      {field.columns.map((column, index) => (
        <div
          key={index}
          className="grid items-end grid-cols-2 gap-2 pb-2 mb-2 border-b border-gray-200 md:grid-cols-8 dark:border-gray-700"
        >
          <TextBox
            label="Key"
            value={column.key}
            maxLength={40}
            onChange={(e) => setColumn(index, { key: e.target.value })}
          />
          <label className="block md:col-span-2">
            <span className={labelClass}>Нэр</span>
            <input
              className={fieldClass}
              value={column.label}
              maxLength={100}
              onChange={(e) => setColumn(index, { label: e.target.value })}
            />
          </label>
          <label className="block">
            <span className={labelClass}>Төрөл</span>
            <select
              className={fieldClass + " pr-8"}
              value={column.type}
              onChange={(e) => setColumn(index, { type: e.target.value })}
            >
              {COLUMN_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </label>
          <TextBox
            label="Дээд урт"
            type="number"
            min={1}
            value={column.maxLength}
            placeholder="анхдагч"
            onChange={(e) => setColumn(index, { maxLength: e.target.value })}
          />
          <label className="block">
            <span className={labelClass}>Жишээ</span>
            <input
              className={fieldClass}
              value={column.placeholder}
              maxLength={200}
              onChange={(e) => setColumn(index, { placeholder: e.target.value })}
            />
          </label>
          <div className="flex flex-wrap items-center gap-1 md:col-span-2">
            <label className="flex items-center gap-1 mr-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                className="text-purple-600 rounded"
                checked={column.required}
                onChange={(e) => setColumn(index, { required: e.target.checked })}
              />
              Заавал
            </label>
            <button
              type="button"
              className={smallButton}
              disabled={!column.key}
              onClick={() => insert("{{" + column.key + "}}")}
            >
              {"{{" + (column.key || "key") + "}}"}
            </button>
            <button type="button" className={smallButton} disabled={index === 0} onClick={() => moveColumn(index, -1)}>
              ↑
            </button>
            <button
              type="button"
              className={smallButton}
              disabled={index === field.columns.length - 1}
              onClick={() => moveColumn(index, 1)}
            >
              ↓
            </button>
            <button
              type="button"
              className={smallButton}
              onClick={() => setColumns(field.columns.filter((item, i) => i !== index))}
            >
              ×
            </button>
          </div>
          {column.type === "select" && (
            <label className="block col-span-2 md:col-span-8">
              <span className={labelClass}>Сонголтууд (нэг мөрөнд нэг)</span>
              <textarea
                rows={3}
                className={areaClass}
                value={column.options.join("\n")}
                onChange={(e) => setColumn(index, { options: e.target.value.split("\n") })}
              ></textarea>
            </label>
          )}
        </div>
      ))}
      <div className="grid grid-cols-2 gap-3 mt-2 md:grid-cols-6">
        <TextBox
          label="Дээд мөр"
          type="number"
          min={1}
          max={50}
          value={field.maxRows}
          placeholder="10"
          onChange={(e) => onChange({ maxRows: e.target.value })}
        />
        <label className="block md:col-span-2">
          <span className={labelClass}>Мөрийн дүн {"{{row_total}}"}</span>
          <select
            className={fieldClass + " pr-8"}
            value={field.total.join(",")}
            onChange={(e) => onChange({ total: e.target.value ? e.target.value.split(",") : [] })}
          >
            <option value="">Байхгүй</option>
            {choices.map((keys) => (
              <option key={keys.join(",")} value={keys.join(",")}>
                {keys.join(" × ")}
              </option>
            ))}
          </select>
          <span className="block mt-1 text-xs text-gray-500 dark:text-gray-400">
            Тоо × үнэ гэх мэт. Сонговол {"{{" + key + "_total}}"}, {"{{" + key + "_total_words}}"},{" "}
            {"{{" + key + "_vat}}"} гарна.
          </span>
        </label>
        <div className="flex flex-wrap items-end gap-2 md:col-span-3">
          <button type="button" className={smallButton} disabled={!field.key} onClick={() => insert(tableOf(field))}>
            Хүснэгт оруулах
          </button>
          <button
            type="button"
            className={smallButton}
            disabled={!field.key}
            onClick={() => insert("{{#" + field.key + "}}{{/" + field.key + "}}")}
          >
            Мөр бүрт давтах хэсэг
          </button>
          <button type="button" className={smallButton} onClick={() => insert("{{n}}")}>
            {"{{n}}"}
          </button>
          {field.total.length > 0 && (
            <button type="button" className={smallButton} onClick={() => insert("{{row_total}}")}>
              {"{{row_total}}"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const TemplateEdit = ({
  location,
  history,
  getTemplate,
  saveTemplate,
  previewTemplate,
  getThemes,
  templates,
}) => {
  const id = new URLSearchParams(location.search).get("id") || "";
  // Offered while typing the category, so templates are not split by a typo: the
  // usual ones and any other the list has loaded
  const categories = [
    ...new Set([...Object.keys(CATEGORY_ICONS), ...templates.map((item) => item.category).filter(Boolean)]),
  ];
  // loading | ready | not_found
  const [state, setState] = useState(id ? "loading" : "ready");
  // The saved template's status and version; null until it is saved once
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState(toForm(NEW_TEMPLATE));
  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  // Every design there is (/template-themes), null while loading
  const [allThemes, setAllThemes] = useState(null);
  // The design the PDF preview is drawn on; the template's first when empty
  const [previewTheme, setPreviewTheme] = useState("");
  const bodyRef = useRef(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      setState("loading");
      const template = await getTemplate(id);
      if (cancelled) return;
      if (!template) {
        setState("not_found");
        return;
      }
      setForm(toForm(template));
      setMeta({ status: template.status, version: template.version });
      setState("ready");
    })();
    return () => {
      cancelled = true;
    };
  }, [id, getTemplate]);

  useEffect(() => {
    let cancelled = false;
    getThemes().then((themes) => {
      if (!cancelled) setAllThemes(themes || []);
    });
    return () => {
      cancelled = true;
    };
  }, [getThemes]);

  // The PDF stays in memory until its object URL is revoked
  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl]
  );

  const set = (name, value) => setForm({ ...form, [name]: value });
  const setField = (index, changes) =>
    setForm({
      ...form,
      fields: form.fields.map((field, i) => (i === index ? { ...field, ...changes } : field)),
    });
  const moveField = (index, delta) => {
    const fields = [...form.fields];
    const [field] = fields.splice(index, 1);
    fields.splice(index + delta, 0, field);
    setForm({ ...form, fields });
  };
  const removeField = (index) =>
    setForm({ ...form, fields: form.fields.filter((field, i) => i !== index) });
  const addField = () => setForm({ ...form, fields: [...form.fields, { ...EMPTY_FIELD }] });

  // Puts text at the cursor in the body. With after, the two go around what is
  // selected, which stays selected.
  const insert = (text, after) => {
    const area = bodyRef.current;
    const start = area ? area.selectionStart : form.body.length;
    const end = area ? area.selectionEnd : form.body.length;
    const inner = after === undefined ? "" : form.body.slice(start, end);
    set("body", form.body.slice(0, start) + text + inner + (after || "") + form.body.slice(end));
    requestAnimationFrame(() => {
      if (!area) return;
      area.focus();
      area.selectionStart = start + text.length;
      area.selectionEnd = start + text.length + inner.length;
    });
  };

  const payload = () => ({
    ...form,
    fields: form.fields.map((field) => {
      const rows = field.type === "rows";
      return {
        ...field,
        options: field.type === "select" ? field.options : [],
        columns: rows
          ? field.columns.map((column) => ({
              ...column,
              options: column.type === "select" ? column.options : [],
            }))
          : [],
        maxRows: rows ? field.maxRows : "",
        total: rows ? field.total : [],
      };
    }),
  });

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const result = await saveTemplate(id || null, payload());
    setSaving(false);
    if (result.errors) {
      setErrors(result.errors);
      window.scrollTo(0, 0);
      return;
    }
    setErrors([]);
    setMeta({ status: result.template.status, version: result.template.version });
    if (!id) history.replace("/template-edit?id=" + result.template._id);
  };

  const onPreview = async () => {
    setPreviewing(true);
    const { name, fields, body, orientation, margins, themes } = payload();
    const theme = themes.includes(previewTheme) ? previewTheme : undefined;
    const url = await previewTemplate({ name, fields, body, orientation, margins, themes, theme });
    setPreviewing(false);
    if (url) setPreviewUrl(url);
  };

  if (state !== "ready") {
    return (
      <Admin>
        <div className="my-6">
          <Link to="/templates" className="text-sm text-purple-600 dark:text-purple-400 hover:underline">
            ← Загвар
          </Link>
          {state === "loading" ? (
            <img
              src={spinner}
              className="my-6"
              style={{ width: "50px", margin: "auto", display: "block" }}
              alt="Loading..."
            />
          ) : (
            <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">Загвар олдсонгүй.</p>
          )}
        </div>
      </Admin>
    );
  }

  const status = meta && (STATUSES[meta.status] || STATUSES.draft);
  const slugLocked = meta && meta.status !== "draft";
  // A number field is also {{key_words}}, its value in words. A rows field is only
  // its section and sums; its columns have their own buttons.
  const keys = form.fields
    .filter((field) => field.key)
    .flatMap((field) => [...(field.type === "rows" ? [] : [field.key]), ...extraKeysOf(field)]);

  // Designs of both orientations may be offered: the one the customer picks turns the
  // page. The template's orientation follows the first, as the backend makes it on
  // saving; it is the admin's own choice only without designs.
  const themeOf = (slug) => (allThemes || []).find((item) => item.slug === slug);
  const themeName = (slug) => {
    const theme = themeOf(slug);
    return theme ? theme.name : slug;
  };
  const orientationName = (value) => (ORIENTATIONS.find((option) => option.value === value) || {}).label || "";
  const setThemes = (themes) => {
    const first = themeOf(themes[0]);
    setForm({ ...form, themes, orientation: first ? first.orientation : form.orientation });
  };
  const toggleTheme = (slug) =>
    setThemes(form.themes.includes(slug) ? form.themes.filter((item) => item !== slug) : [...form.themes, slug]);
  const makeDefault = (slug) => setThemes([slug, ...form.themes.filter((item) => item !== slug)]);
  // The chosen designs of one orientation, in the order the customer sees them
  const chosenOf = (orientation) => form.themes.filter((slug) => (themeOf(slug) || {}).orientation === orientation);
  const bothKinds = ORIENTATIONS.every((option) => chosenOf(option.value).length > 0);
  const gone = allThemes === null ? [] : form.themes.filter((slug) => !themeOf(slug));

  return (
    <Admin>
      <form onSubmit={onSave}>
        <div className="flex flex-wrap items-center justify-between gap-4 my-6">
          <div>
            <Link to="/templates" className="text-sm text-purple-600 dark:text-purple-400 hover:underline">
              ← Загвар
            </Link>
            <h2 className="mt-1 text-2xl font-semibold text-gray-700 dark:text-gray-200">
              {id ? form.name || "Загвар" : "Шинэ загвар"}
              {status && (
                <span
                  className={
                    "inline-block px-2 py-1 ml-3 text-xs font-semibold leading-tight align-middle rounded-full " +
                    status.badge
                  }
                >
                  {status.label} · v{meta.version}
                </span>
              )}
            </h2>
          </div>
          <button type="submit" className={primaryButton} disabled={saving}>
            {saving ? "Хадгалж байна..." : "Хадгалах"}
          </button>
        </div>

        {errors.length > 0 && (
          <div className="p-4 mb-6 text-sm text-red-700 bg-red-100 rounded-lg dark:bg-red-700 dark:text-red-100">
            <p className="mb-1 font-semibold">Хадгалсангүй:</p>
            <ul className="ml-5 list-disc">
              {errors.map((error, index) => (
                <li key={index}>
                  {PARAM_LABELS[error.param] ? PARAM_LABELS[error.param] + ": " : ""}
                  {error.msg}
                </li>
              ))}
            </ul>
          </div>
        )}

        <Section title="Ерөнхий">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            <TextBox
              label="Нэр"
              value={form.name}
              maxLength={100}
              onChange={(e) => set("name", e.target.value)}
              hint="Хэрэглэгч болон киоск энэ нэрийг харна."
            />
            <TextBox
              label="Slug (SKU)"
              value={form.slug}
              maxLength={60}
              disabled={slugLocked}
              onChange={(e) => set("slug", e.target.value)}
              hint={
                slugLocked
                  ? "Нийтлэгдсэн тул өөрчлөхгүй: холбоос, статистик үүгээр явна."
                  : "Латин жижиг үсэг, цифр, зураас. Жишээ: orgodol"
              }
            />
            <TextBox
              label="Ангилал"
              value={form.category}
              maxLength={50}
              list="template-categories"
              onChange={(e) => set("category", e.target.value)}
              hint="Жагсаалтад бүлэглэнэ. Байгаагаас сонгох эсвэл шинээр бичнэ."
            />
            <datalist id="template-categories">
              {categories.map((name) => (
                <option key={name} value={name} />
              ))}
            </datalist>
            <label className="block md:col-span-2 xl:col-span-3">
              <span className={labelClass}>Тайлбар</span>
              <input
                className={fieldClass}
                value={form.description}
                maxLength={500}
                onChange={(e) => set("description", e.target.value)}
              />
            </label>
            <TextBox
              label="Дараалал"
              type="number"
              value={form.order}
              onChange={(e) => set("order", e.target.value)}
              hint="Бага нь жагсаалтад эхэнд."
            />
            <TextBox
              label="Үнэ (нэг хувь, ₮)"
              type="number"
              min={0}
              value={form.price}
              onChange={(e) => set("price", e.target.value)}
              hint="Киоск (1.0.13+) нэг хувийг хуудасны тоо, өнгөнөөс үл хамааран энэ үнээр авна. Хоосон эсвэл 0 бол энгийн хэвлэлийн үнээр. Өөрчлөлт шинээр үүсэх PDF-д л үйлчилнэ."
            />
            <label className="block">
              <span className={labelClass}>Хэвлэх цаас</span>
              <select className={fieldClass} value={form.paperMedia} onChange={(e) => set("paperMedia", e.target.value)}>
                <option value="plain-a4">Энгийн A4</option>
                <option value="matte-a4-200">A4 цагаан матт · inkjet · 200 г/м²</option>
              </select>
              <span className="block mt-1 text-xs text-gray-500">Матт цаастай, тохируулсан киоск дээр нэг талд хэвлэнэ. Шинээр үүсэх PDF-д үйлчилнэ.</span>
            </label>
            <label className="block">
              <span className={labelClass}>Хуудас (A4)</span>
              <select
                className={fieldClass + " pr-8"}
                value={form.orientation}
                onChange={(e) => set("orientation", e.target.value)}
                disabled={form.themes.length > 0}
              >
                {ORIENTATIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {form.themes.length > 0 && (
                <span className="block mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Хэрэглэгчийн сонгосон дизайнаар. Анхдагч дизайнаар{" "}
                  {orientationName(form.orientation).toLowerCase()}.
                </span>
              )}
            </label>
            <label className="block md:col-span-2">
              <span className={labelClass}>Захын зай</span>
              <select
                className={fieldClass + " pr-8"}
                value={form.margins}
                onChange={(e) => set("margins", e.target.value)}
              >
                {MARGINS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <span className="block mt-1 text-xs text-gray-500 dark:text-gray-400">
                Өргөмжлөл, зарын хуудсанд нарийн. Хэвтээ загварыг нийтлэхээс өмнө киоск дээр туршиж хэвлээрэй.
              </span>
            </label>
          </div>
        </Section>

        <Section
          title="Дизайн"
          right={
            <Link to="/template-themes" className="text-sm text-purple-600 dark:text-purple-400 hover:underline">
              Дизайнууд удирдах →
            </Link>
          }
        >
          {allThemes === null ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Ачаалж байна...</p>
          ) : (
            <>
              <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
                Сонгосон дизайнуудаас хэрэглэгч зургаар нь сонгоно: босоо дизайн сонговол хуудас босоо, хэвтээ
                бол хэвтээ. Эхнийх нь анхдагч. Нэгийг ч сонгоогүй бол цагаан хуудас.
              </p>
              {ORIENTATIONS.map((kind) => {
                const offered = allThemes.filter((theme) => theme.orientation === kind.value);
                const chosen = chosenOf(kind.value);
                return (
                  <div key={kind.value} className="mb-5 last:mb-0">
                    <h4 className="mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase dark:text-gray-400">
                      {kind.label} дизайн
                      <span className="ml-2 font-normal normal-case">
                        {chosen.length} / {offered.length} сонгосон
                      </span>
                    </h4>
                    {offered.length === 0 ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400">{kind.label} дизайн алга.</p>
                    ) : (
                      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-6">
                        {offered.map((theme) => {
                          const index = form.themes.indexOf(theme.slug);
                          // Its number among the chosen of its kind, as the customer sees them
                          const place = chosen.indexOf(theme.slug);
                          return (
                            <div
                              key={theme.slug}
                              className={
                                "overflow-hidden border-2 rounded-lg " +
                                (index !== -1 ? "border-purple-500" : "border-gray-200 dark:border-gray-700")
                              }
                            >
                              <button
                                type="button"
                                className="relative block w-full focus:outline-none"
                                onClick={() => toggleTheme(theme.slug)}
                                aria-pressed={index !== -1}
                              >
                                <img
                                  src={theme.thumbnail}
                                  alt=""
                                  className={"block object-cover w-full" + (index !== -1 ? "" : " opacity-60")}
                                  style={{ aspectRatio: theme.orientation === "portrait" ? "210 / 297" : "297 / 210" }}
                                />
                                {index !== -1 && (
                                  <span className="absolute top-0 left-0 px-2 py-1 m-1 text-xs font-semibold text-white bg-purple-600 rounded">
                                    {index === 0 ? "Анхдагч" : place + 1}
                                  </span>
                                )}
                              </button>
                              <div className="flex items-center justify-between gap-1 px-2 py-1">
                                <span className="text-xs text-gray-700 truncate dark:text-gray-300">{theme.name}</span>
                                {index > 0 && (
                                  <button
                                    type="button"
                                    className="text-xs text-purple-600 whitespace-nowrap dark:text-purple-400 hover:underline"
                                    onClick={() => makeDefault(theme.slug)}
                                  >
                                    Анхдагч болгох
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
              {bothKinds && (
                <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
                  Хоёр чиглэлийн дизайнтай: агуулга босоо, хэвтээ хуудсанд хоёуланд нь багтаж байгааг PDF-ээр
                  шалгаарай. Ялгаатай хэсгийг {"{{#portrait}}...{{/portrait}}"},{" "}
                  {"{{#landscape}}...{{/landscape}}"} дотор бичнэ. Хэвтээ хуудсыг нийтлэхээс өмнө киоск дээр туршиж
                  хэвлээрэй.
                </p>
              )}
            </>
          )}
          {gone.length > 0 && (
            <p className="mt-3 text-xs text-red-600 dark:text-red-400">
              Устсан дизайн сонгогдсон байна: {gone.join(", ")}.
              <button
                type="button"
                className="ml-1 underline"
                onClick={() => setThemes(form.themes.filter((slug) => themeOf(slug)))}
              >
                Хасах
              </button>
            </p>
          )}
        </Section>

        <Section
          title="Талбарууд"
          right={
            <button type="button" className={smallButton} onClick={addField}>
              + Талбар нэмэх
            </button>
          }
        >
          {form.fields.length === 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400">Талбар алга.</p>
          )}
          {form.fields.map((field, index) => (
            <div
              key={index}
              className="p-3 mb-3 border border-gray-200 rounded-lg dark:border-gray-700"
            >
              <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
                <TextBox
                  label="Key"
                  value={field.key}
                  maxLength={40}
                  onChange={(e) => setField(index, { key: e.target.value })}
                />
                <label className="block col-span-2">
                  <span className={labelClass}>Асуулт</span>
                  <input
                    className={fieldClass}
                    value={field.label}
                    maxLength={100}
                    onChange={(e) => setField(index, { label: e.target.value })}
                  />
                </label>
                <label className="block">
                  <span className={labelClass}>Төрөл</span>
                  <select
                    className={fieldClass + " pr-8"}
                    value={field.type}
                    onChange={(e) =>
                      setField(index, {
                        type: e.target.value,
                        ...(e.target.value === "rows" && field.columns.length === 0 ? NEW_ROWS : {}),
                      })
                    }
                  >
                    {FIELD_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </label>
                {field.type === "rows" ? (
                  <span></span>
                ) : (
                  <TextBox
                    label="Дээд урт"
                    type="number"
                    min={1}
                    value={field.maxLength}
                    placeholder="анхдагч"
                    onChange={(e) => setField(index, { maxLength: e.target.value })}
                  />
                )}
                <label className="flex items-center gap-2 mt-5 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    className="text-purple-600 rounded"
                    checked={field.required}
                    onChange={(e) => setField(index, { required: e.target.checked })}
                  />
                  Заавал
                </label>
                {field.type !== "rows" && (
                  <label className="block col-span-2 md:col-span-3">
                    <span className={labelClass}>Жишээ (placeholder)</span>
                    <input
                      className={fieldClass}
                      value={field.placeholder}
                      maxLength={200}
                      onChange={(e) => setField(index, { placeholder: e.target.value })}
                    />
                  </label>
                )}
                <label className="block col-span-2 md:col-span-3">
                  <span className={labelClass}>Тайлбар</span>
                  <input
                    className={fieldClass}
                    value={field.help}
                    maxLength={300}
                    onChange={(e) => setField(index, { help: e.target.value })}
                  />
                </label>
                {field.type === "select" && (
                  <label className="block col-span-2 md:col-span-6">
                    <span className={labelClass}>Сонголтууд (нэг мөрөнд нэг)</span>
                    <textarea
                      rows={4}
                      className={areaClass}
                      value={field.options.join("\n")}
                      onChange={(e) => setField(index, { options: e.target.value.split("\n") })}
                    ></textarea>
                  </label>
                )}
                {field.type === "rows" && (
                  <ColumnsEditor field={field} onChange={(changes) => setField(index, changes)} insert={insert} />
                )}
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {field.type !== "rows" && (
                  <>
                    <button
                      type="button"
                      className={smallButton}
                      disabled={!field.key}
                      onClick={() => insert("{{" + field.key + "}}")}
                    >
                      {"{{" + (field.key || "key") + "}}"} оруулах
                    </button>
                    <button
                      type="button"
                      className={smallButton}
                      disabled={!field.key}
                      onClick={() => insert("{{#" + field.key + "}}{{/" + field.key + "}}")}
                    >
                      Хоосон бол нуух хэсэг
                    </button>
                  </>
                )}
                <span className="flex-1"></span>
                <button
                  type="button"
                  className={smallButton}
                  disabled={index === 0}
                  onClick={() => moveField(index, -1)}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className={smallButton}
                  disabled={index === form.fields.length - 1}
                  onClick={() => moveField(index, 1)}
                >
                  ↓
                </button>
                <button type="button" className={smallButton} onClick={() => removeField(index)}>
                  Хасах
                </button>
              </div>
            </div>
          ))}
        </Section>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <Section title="Агуулга (HTML)">
            <div className="flex flex-wrap gap-2 mb-3">
              {keys.map((key) => (
                <button
                  key={key}
                  type="button"
                  className={smallButton}
                  onClick={() => insert("{{" + key + "}}")}
                >
                  {"{{" + key + "}}"}
                </button>
              ))}
              <button type="button" className={smallButton} onClick={() => insert("{{today}}")}>
                {"{{today}}"}
              </button>
              <button type="button" className={smallButton} onClick={() => insert("{{year}}")}>
                {"{{year}}"}
              </button>
              <button type="button" className={smallButton} onClick={() => insert("{{theme_text}}")}>
                {"{{theme_text}}"}
              </button>
              <button type="button" className={smallButton} onClick={() => insert("{{theme_accent}}")}>
                {"{{theme_accent}}"}
              </button>
              {bothKinds &&
                ORIENTATIONS.map(({ value }) => (
                  <button
                    key={value}
                    type="button"
                    className={smallButton}
                    onClick={() => insert("{{#" + value + "}}", "{{/" + value + "}}")}
                  >
                    {"{{#" + value + "}}...{{/" + value + "}}"}
                  </button>
                ))}
            </div>
            <textarea
              ref={bodyRef}
              rows={24}
              spellCheck={false}
              className={areaClass + " font-mono text-xs"}
              value={form.body}
              onChange={(e) => set("body", e.target.value)}
            ></textarea>
            <ul className="mt-3 ml-5 space-y-1 text-xs text-gray-500 list-disc dark:text-gray-400">
              <li>
                {"{{key}}"} талбарын утга; {"{{today}}"} өнөөдөр (2026.09.27); {"{{year}}"} он.
              </li>
              <li>
                {"{{#key}} ... {{/key}}"}: тэр талбар хоосон бол энэ хэсэг гарахгүй.
              </li>
              <li>
                Тоо талбар: {"{{key}}"} нь 1,500,000; {"{{key_words}}"} нь үсгээр, нэрийн өмнө
                ордог хэлбэрээр: {"{{key}} ({{key_words}}) төгрөг"} → 1,500,000 (нэг сая таван
                зуун мянган) төгрөг.
              </li>
              <li>
                Мөрүүд (хүснэгт): {"{{#items}} ... {{/items}}"} мөр бүрт давтагдана. Дотор нь баганууд,{" "}
                {"{{n}}"} мөрийн дугаар, {"{{row_total}}"} мөрийн дүн. Гадна нь {"{{items_count}}"} мөрийн
                тоо, {"{{items_total}}"} нийт дүн, {"{{items_total_words}}"} үсгээр, {"{{items_vat}}"} нийт
                дүнд багтсан 10% НӨАТ. {"{{#items_count}} ... {{/items_count}}"}: мөргүй бол нуух хэсэг.
              </li>
              <li>
                Хэмжээг pt-ээр: font-size:14pt, margin-top:24pt. Div-ийн margin ажиллахгүй, p дээр тавина.
              </li>
              <li>
                Tag: p, div, span, br, hr, b, i, u, h1–h6, table, ul, ol, img (зөвхөн data:image).
              </li>
              <li>
                Хүснэгтийн багана: {"<table data-pdfmake='{\"widths\":[\"*\",240],\"layout\":\"noBorders\"}'>"}
              </li>
              <li>
                Шинэ хуудас: {'<div class="page-break"></div>'}. Фонт: Times New Roman, эсвэл font-family:Arial.
              </li>
              <li>
                A4. Босоо, хэвтээг хэрэглэгчийн сонгосон дизайн шийднэ, дизайнгүй бол «Ерөнхий» хэсгийн
                сонголт. Захын зайг «Ерөнхий» хэсэгт сонгоно.
              </li>
              <li>
                {"{{#portrait}} ... {{/portrait}}"} зөвхөн босоо, {"{{#landscape}} ... {{/landscape}}"} зөвхөн
                хэвтээ хуудсанд гарна. Жишээ:{" "}
                {"margin-top:{{#portrait}}70{{/portrait}}{{#landscape}}20{{/landscape}}pt"}
              </li>
              <li>
                Дизайны өнгө: {"style=\"color:{{theme_text}}\""} текст, {"{{theme_accent}}"} гарчиг.
                Дизайнгүй бол хар.
              </li>
            </ul>
          </Section>

          <Section
            title="PDF урьдчилан харах"
            right={
              <div className="flex items-center gap-2">
                {form.themes.length > 1 && (
                  <select
                    className={fieldClass + " pr-8 w-auto"}
                    value={form.themes.includes(previewTheme) ? previewTheme : ""}
                    onChange={(e) => setPreviewTheme(e.target.value)}
                    aria-label="Аль дизайнаар харах"
                  >
                    <option value="">Анхдагч дизайн</option>
                    {form.themes.map((slug) => (
                      <option key={slug} value={slug}>
                        {themeName(slug)}
                        {bothKinds && themeOf(slug) ? " · " + orientationName(themeOf(slug).orientation) : ""}
                      </option>
                    ))}
                  </select>
                )}
                <button type="button" className={secondaryButton} disabled={previewing} onClick={onPreview}>
                  {previewing ? "Үүсгэж байна..." : previewUrl ? "Шинэчлэх" : "PDF харах"}
                </button>
              </div>
            }
          >
            {previewUrl ? (
              <iframe
                title="PDF"
                src={previewUrl}
                className="w-full bg-white border border-gray-200 rounded dark:border-gray-700"
                style={{ height: 900 }}
              ></iframe>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Хадгалаагүй өөрчлөлтийг ч харуулна. Талбар бүрийг жишээ утгаар нь, жишээгүй бол
                [асуултаар] нь бөглөнө.
              </p>
            )}
          </Section>
        </div>
      </form>
    </Admin>
  );
};

TemplateEdit.propTypes = {
  getTemplate: PropTypes.func.isRequired,
  saveTemplate: PropTypes.func.isRequired,
  previewTemplate: PropTypes.func.isRequired,
  getThemes: PropTypes.func.isRequired,
  templates: PropTypes.array.isRequired,
};

// The list's templates, when it was opened first: their categories are offered
const mapStateToProps = (state) => ({ templates: state.template.templates });

export default connect(mapStateToProps, { getTemplate, saveTemplate, previewTemplate, getThemes })(
  TemplateEdit
);
